import {useCallback, useContext, useEffect, useMemo, useState} from "react";
import useSupplyForm from "./useSupplyForm";
import useDialog from "../../../../hooks/useDialog";
import {
    postSearchInitialSupply,
    postSearchSupply,
    postSearchSupplyBySupplier,
    postUpdateSupply
} from "../../../../http/api/SupplyAPI";
import {ScreenStatusContext} from "../../../../context/ScreenStatusContext";
import {flushSync} from "react-dom";
import useProductHistoryModal from "./useProductHistoryModal";

/**
 * 1ページ当たり商品行数
 */
export const SUPPLY_TABLE_ROW_COUNT_PER_PAGE = 20;

/**
 * 発注入力画面情報管理hook
 */
const useSupplyListForm = () => {
    const [deliveryDate, setDeliveryDate] = useState(null);

    const [supplierGroups, setSupplierGroups] = useState([]);
    const [supplierGroupSelectValue, setSupplierGroupSelectValue] = useState("1");

    const [addingProduct, setAddingProduct] = useState({});

    const [enabledFilteringWithSupply, setEnabledFilteringWithSupply] = useState(false);
    const [enabledDisplayRemarks, setEnabledDisplayRemarks] = useState(true);

    const [page, setPage] = useState(1);

    const [supplyDivisions, setSupplyDivisions] = useState([]);
    const [supplyDivisionSelectValue, setSupplyDivisionSelectValue] = useState("1");
    const [isProceeding, setProceeding] = useState(false);

    const addProductDialog = useDialog();
    const addProductMasterDialog = useDialog();
    const addProductDetailMasterDialog = useDialog();

    const printPreviewDialog = useDialog();

    // Combined Right Sidebar state
    const [rightSidebarOpen, setRightSidebarOpen] = useState(false);
    const [rightSidebarMode, setRightSidebarMode] = useState("addProduct"); // "addProduct" | "history"
    const [sidebarRefreshTrigger, setSidebarRefreshTrigger] = useState(0);
    const [sidebarScrollToProductId, setSidebarScrollToProductId] = useState(null);
    const [supplyHistoryAll, setSupplyHistoryAll] = useState([]);
    // 発注確定 (submit) のたびに +1 する。CombinedSidebar 側で変化を検知し、
    // 検索フォームのクリアとサイドバーのクローズを行う。
    const [submitVersion, setSubmitVersion] = useState(0);

    const toggleRightSidebar = () => setRightSidebarOpen(prev => !prev);
    const closeSidebar = () => setRightSidebarOpen(false);
    const openSidebar = () => setRightSidebarOpen(true);

    const switchRightSidebarMode = (mode) => {
        if (rightSidebarOpen && rightSidebarMode === mode) {
            // If already open with same mode, close it
            setRightSidebarOpen(false);
        } else {
            // Open with new mode or switch mode
            setRightSidebarMode(mode);
            setRightSidebarOpen(true);
        }
    };

    const {isChanged, setChanged} = useContext(ScreenStatusContext);

    const {
        supplyForm,
        targetSupplier,
        setSupplyInfo,
        addSupplyDetail,
        addSupplyDetailAt,
        hasProductWithSupply,
        hasError,
        changeOriginAt,
        hideSupplyDetail,
        showSupplyDetail,
        showSupplyDetailAt
    } = useSupplyForm();

    const productHistoryModal = useProductHistoryModal();

    // Combined hide handler - updates both main table and sidebar
    const handleCombinedHide = useCallback((product) => {
        hideSupplyDetail(product);
        productHistoryModal.handleHide(product);
    }, [hideSupplyDetail, productHistoryModal]);

    // Combined show handler - updates both main table and sidebar
    const handleCombinedBack = useCallback((product) => {
        showSupplyDetail(product);
        productHistoryModal.handleBack(product);
    }, [showSupplyDetail, productHistoryModal]);

    // Combined show handler at specific index
    const handleCombinedBackAt = useCallback((product, atIndex) => {
        showSupplyDetailAt(product, atIndex);
        productHistoryModal.handleBack(product);
    }, [showSupplyDetailAt, productHistoryModal]);

    const errorMessages = useMemo(() => {

        const messages = [];

        if (hasError()) {
            messages.push("入力にエラーがあります。");
        }

        return messages;

    }, [supplyForm]);

    /**
     * 入荷日の選択を変更する
     * @param 入荷日
     */
    const changeDeliveryDate = useCallback(async (deliveryDateInput) => {
        setDeliveryDate(deliveryDateInput);

        if (isChanged && !await submitWhenConfirmed()) {
            setDeliveryDate(deliveryDate);
            return;
        }
        const selectedSupplierGroup = getSelectingSupplierGroup(supplierGroups, parseInt(supplierGroupSelectValue));
        await search(deliveryDateInput, selectedSupplierGroup.commodityOwnerCd, supplyDivisionSelectValue);
    }, [isChanged, deliveryDate, supplierGroups, supplierGroupSelectValue, supplyDivisionSelectValue]);

    /**
     * 仕入先(グループ)選択を変更する
     * @param value 仕入先選択番号　※セレクトボックスの番号
     */
    const changeSupplierGroupSelect = useCallback(async (value) => {
        if (isChanged && !await submitWhenConfirmed()) {
            setSupplierGroupSelectValue(supplierGroupSelectValue);
            return;
        }
        setSupplierGroupSelectValue(value);
        const selectedSupplierGroup = getSelectingSupplierGroup(supplierGroups, parseInt(value));

        await searchSupplyBySupplier(selectedSupplierGroup.commodityOwnerCd, deliveryDate);
    }, [isChanged, supplierGroups, supplierGroupSelectValue, deliveryDate]);

    /**
     * 発注区分の選択を変更する
     * @param value 発注区分選択番号※セレクトボックスの番号
     */
    const changeSupplyDivisionSelect = async (value) => {
        if (isChanged && !await submitWhenConfirmed()) {
            setSupplyDivisionSelectValue(supplyDivisionSelectValue);
            return;
        }
        setSupplyDivisionSelectValue(value);
        const selectedSupplierGroup = getSelectingSupplierGroup(supplierGroups, parseInt(supplierGroupSelectValue));
        await search(deliveryDate, selectedSupplierGroup.commodityOwnerCd, value);
    }

    /**
     * supplyHistory から supplyDetails に存在しない商品を抽出して履歴リストに設定する
     */
    const updateHistoryFromSupplyInfo = (supplyInfo) => {
        const supplyDetails = supplyInfo.supplyDetails ?? [];
        const supplyHistory = supplyInfo.supplyHistory ?? [];
        setSupplyHistoryAll(supplyHistory);

        // supplyDetails に存在する商品のキーセットを作成
        const detailKeys = new Set(
            supplyDetails.map((d) => makeHistoryKey(d))
        );

        // supplyHistory のうち supplyDetails にないものが履歴リスト
        // isHidden: true を設定して、History サイドバーに表示されるようにする
        const historyOnly = supplyHistory
            .filter((h) => !detailKeys.has(makeHistoryKey(h)))
            .map((h) => ({...h, isHidden: true}));

        productHistoryModal.setHistoryData(historyOnly);
    };

    /**
     * 発注情報を検索する
     * @param deliveryDate 入荷日
     * @param commodityOwnerCd 荷主コード
     * @param isMainSupplier 主要仕入先か？
     */
    const search = async (deliveryDate, commodityOwnerCd, supplyDivisionId) => {
        try {
            const supplyRes = await postSearchSupply({
                deliveryDate, commodityOwnerCd, supplyDivisionId
            });

            setSupplyInfo(supplyRes.supplyInfo);
            updateHistoryFromSupplyInfo(supplyRes.supplyInfo);
            setEnabledFilteringWithSupply(false);
            setPage(1);
        } catch (e) {
            alert("データが取得できませんでした。")
        }
    }

    /**
     * 初期検索する
     */
    const searchInitial = async () => {
        try {
            const supplyRes = await postSearchInitialSupply({});

            setSupplierGroups(supplyRes.supplierGroups);
            setSupplyDivisions(supplyRes.supplyDivisions);
            setSupplyDivisionSelectValue(supplyRes.supplyInfo.supplyDivision.supplyDivisionId)

            setDeliveryDate(supplyRes.supplyInfo.deliveryDate);
            setSupplyInfo(supplyRes.supplyInfo);
            updateHistoryFromSupplyInfo(supplyRes.supplyInfo);
            setEnabledDisplayRemarks(true);
            setEnabledFilteringWithSupply(false);
            setPage(1);
        } catch (e) {
            alert("データが取得できませんでした。");
        }
    }

    /**
     * 仕入先情報から発注情報を検索する
     */
    const searchSupplyBySupplier = async (commodityOwnerCd, deliveryDate) => {
        try {
            const supplyRes = await postSearchSupplyBySupplier({commodityOwnerCd, deliveryDate});

            setSupplyDivisions(supplyRes.supplyDivisions);
            setSupplyDivisionSelectValue(supplyRes.supplyInfo.supplyDivision.supplyDivisionId)

            setDeliveryDate(supplyRes.supplyInfo.deliveryDate);
            setSupplyInfo(supplyRes.supplyInfo);
            updateHistoryFromSupplyInfo(supplyRes.supplyInfo);
            setEnabledDisplayRemarks(true);
            setEnabledFilteringWithSupply(false);
            setPage(1);
        } catch (e) {
            alert("データが取得できませんでした。");
        }
    }

    /**
     * 商品追加ダイアログを開く
     */
    const openAddProductDialog = async () => {

        const addProductResult = await addProductDialog.open();

        if (!addProductResult) {
            return;
        }

        const product = addProductResult.product;

        if (addProductResult.status == "SUBMITTED") {
            addSupplyDetail(buildSupplyDetailFromProduct(product.addingProduct, product.tabPos));
        }
        if (addProductResult.status == "ADD_PRODUCT_MASTER") {
            setAddingProduct(product);
            await openAddProductMasterDialog();
        }
        if (addProductResult.status == "ADD_PRODUCT_DETAIL_MASTER") {
            setAddingProduct(product);
            await openAddProductDetailMasterDialog();
        }
    }

    /**
     * 商品マスタ追加ダイアログを開く
     */
    const openAddProductMasterDialog = async () => {
        const addProductMasterResult = await addProductMasterDialog.open();
        if (!addProductMasterResult) {
            return;
        }

        const addingProduct = addProductMasterResult.product;

        if (addProductMasterResult.status == "ADD_PRODUCT_DETAIL_MASTER") {
            setAddingProduct(addingProduct);
            await openAddProductDetailMasterDialog();
        }
        if (addProductMasterResult.status == "RETURN_ADD_PRODUCT") {
            await openAddProductDialog();
        }
    }

    /**
     * 商品明細マスタ追加ダイアログを開く
     */
    const openAddProductDetailMasterDialog = async () => {
        const addProductDetailMasterResult = await addProductDetailMasterDialog.open();
        if (!addProductDetailMasterResult) {
            return;
        }

        const addingProduct = addProductDetailMasterResult.product;

        if (addProductDetailMasterResult.status == "REOPEN_ADD_PRODUCT") {
            setAddingProduct(addingProduct);
            await openAddProductDialog();
        }
        if (addProductDetailMasterResult.status == "RETURN_ADD_PRODUCT") {
            await openAddProductDialog();
        }
    }

    /**
     * サイドバーから商品を追加する
     */
    const handleAddProductFromSidebar = (addProductData, tabPosValue) => {
        addSupplyDetail(buildSupplyDetailFromProduct(addProductData, tabPosValue));
    };

    /**
     * サイドバーから指定インデックスに商品を追加する（ドラッグ&ドロップ用）
     */
    const handleAddProductFromSidebarAt = (addProductData, tabPosValue, atIndex) => {
        addSupplyDetailAt(buildSupplyDetailFromProduct(addProductData, tabPosValue), atIndex);
    };

    /**
     * サイドバーから商品明細マスタ追加ダイアログを開く（サイドバー用）
     * 旧商品追加ダイアログには遷移せず、サイドバーに戻る
     */
    const openAddProductDetailMasterDialogFromSidebar = async () => {
        const prevMode = rightSidebarMode;
        const result = await addProductDetailMasterDialog.open();
        if (!result) return;

        // "REOPEN_ADD_PRODUCT" / "RETURN_ADD_PRODUCT" → サイドバーに戻る（旧ダイアログは開かない）
        // 商品リストを更新してスクロール
        setSidebarRefreshTrigger((prev) => prev + 1);
        if (result.product?.productId) {
            setSidebarScrollToProductId(result.product.productId);
        }
        setRightSidebarOpen(true);
        setRightSidebarMode(prevMode === "combined" ? "combined" : "addProduct");
    };

    /**
     * サイドバーから商品マスタ追加ダイアログを開く（サイドバー用）
     * 旧商品追加ダイアログには遷移せず、サイドバーに戻る
     */
    const handleOpenAddProductMasterFromSidebar = async (product) => {
        const prevMode = rightSidebarMode;
        setAddingProduct(product ?? {});
        const result = await addProductMasterDialog.open();
        if (!result) return;

        const resultProduct = result.product;

        if (result.status === "ADD_PRODUCT_DETAIL_MASTER") {
            // 商品マスタ追加 → 商品明細マスタ追加（この流れは維持）
            setAddingProduct(resultProduct);
            await openAddProductDetailMasterDialogFromSidebar();
            return;
        }

        // "RETURN_ADD_PRODUCT" → サイドバーに戻る（旧ダイアログは開かない）
        setSidebarRefreshTrigger((prev) => prev + 1);
        if (resultProduct?.productId) {
            setSidebarScrollToProductId(resultProduct.productId);
        }
        setRightSidebarOpen(true);
        setRightSidebarMode(prevMode === "combined" ? "combined" : "addProduct");
    };

    /**
     * サイドバーから商品明細マスタ追加ダイアログを開く（サイドバー用）
     */
    const handleOpenAddProductDetailMasterFromSidebar = async (product) => {
        setAddingProduct(product ?? {});
        await openAddProductDetailMasterDialogFromSidebar();
    };

    /**
     * PDFプレビューダイアログを開く
     */
    const openPreviewDialog = async () => {

        try {
            setProceeding(true);
            await postUpdateSupply({
                deliveryDate,
                commodityOwnerCd: targetSupplier.commodityOwnerCd,
                supplyDivisionId: supplyDivisionSelectValue,
                supplyDetails: supplyForm.forms
            });

            await search(deliveryDate, targetSupplier.commodityOwnerCd, supplyDivisionSelectValue);
            setChanged(false);
            setProceeding(false);
            
            await printPreviewDialog.open();
        } catch (e) {
            console.log(e);
            alert("仕入情報を更新できませんでした。")
        } finally {
            setProceeding(false);
        }

    }

    /**
     * PDFプレビューダイアログを閉じる
     */
    const closePreviewDialog = (v) => async (e) => {
        printPreviewDialog.onClose(v)(e);
    }

    /**
     * 仕入情報を更新する
     */
    const submit = async () => {
        try {
            setProceeding(true);
            await postUpdateSupply({
                deliveryDate,
                commodityOwnerCd: targetSupplier.commodityOwnerCd,
                supplyDivisionId: supplyDivisionSelectValue,
                supplyDetails: supplyForm.forms.map((detail, index) => ({
                    ...detail,
                    displayOrder: index + 1,
                    isDelete: detail.isDelete ?? false,
                }))
            });

            await search(deliveryDate, targetSupplier.commodityOwnerCd, supplyDivisionSelectValue);
            setChanged(false);
            setSubmitVersion((v) => v + 1);

            alert("仕入情報を更新しました。")
        } catch (e) {
            console.log(e);
            alert("仕入情報を更新できませんでした。")
        } finally {
            setProceeding(false);
        }
    }

    /**
     * 確定確認をユーザに行い、OKを押したら更新する
     */
    const submitWhenConfirmed = async () => {

        if (window.confirm("発注情報の入力が変更されています。確定しますか?")) {
            try {

                setProceeding(true);
                await postUpdateSupply({
                    deliveryDate,
                    commodityOwnerCd: targetSupplier.commodityOwnerCd,
                    supplyDivisionId: supplyDivisionSelectValue,
                    supplyDetails: supplyForm.forms.map((detail, index) => ({
                        ...detail,
                        displayOrder: index + 1,
                        isDelete: detail.isDelete ?? false,
                    }))
                });
                setSubmitVersion((v) => v + 1);
                return true;
            } catch (e) {
                console.log(e);
                alert("仕入情報を更新できませんでした。");
            } finally {
                setProceeding(false)
            }
        }

        return false;
    }

    /**
     * 初期表示時、初期検索をかける
     */
    useEffect(() => {
        (async () => {
            searchInitial();
        })();
    }, []);

    /**
     * ページ数を指定して移動する
     * @param page ページ数
     */
    const movePage = (page) => {
        flushSync(
            () => {
                setPage(page)
            }
        );
    }

    /** 検索フィルター後発注（isDelete は非表示） */
    const filteredSupplyForms
        = supplyForm.forms
        .filter((form) =>
            !form.isDelete
            && (!enabledFilteringWithSupply || form.supplyQuantityDetails.some((detail) => detail.supplyQuantity > 0))
        );

    /**
     * 前のページに移動する（ドラッグ中にホバー時）
     */
    const navigateToPreviousPage = useCallback(() => {
        if (page <= 1) return;

        flushSync(() => {
            setPage(page - 1);
        });
    }, [page]);

    /**
     * 次のページに移動する（ドラッグ中にホバー時）
     */
    const navigateToNextPage = useCallback(() => {
        const maxPageValue = Math.max(1, Math.ceil(filteredSupplyForms.length / SUPPLY_TABLE_ROW_COUNT_PER_PAGE));
        if (page >= maxPageValue) return;

        flushSync(() => {
            setPage(page + 1);
        });
    }, [page, filteredSupplyForms]);

    /**
     * 指定のページに移動する（ドラッグ中にホバー時）
     */
    const navigateToPage = useCallback((targetPage) => {
        const maxPageValue = Math.max(1, Math.ceil(filteredSupplyForms.length / SUPPLY_TABLE_ROW_COUNT_PER_PAGE));
        if (targetPage < 1 || targetPage > maxPageValue || targetPage === page) return;

        flushSync(() => {
            setPage(targetPage);
        });
    }, [page, filteredSupplyForms]);

    return {
        deliveryDate,
        supplierGroups,
        supplierGroupSelectValue,
        targetSupplier,
        supplyDivisions,
        supplyDivisionSelectValue,
        changeSupplyDivisionSelect,
        supplyForm,
        pageSupplyForm: {
            ...supplyForm,
            forms:
                filteredSupplyForms
                    .slice(
                        SUPPLY_TABLE_ROW_COUNT_PER_PAGE * (page - 1),
                        SUPPLY_TABLE_ROW_COUNT_PER_PAGE * page
                    )
        },
        errorMessages,
        submit,
        setSupplierGroupSelectValue,
        changeSupplierGroupSelect,
        changeDeliveryDate,
        enabledFilteringWithSupply,
        setEnabledFilteringWithSupply,
        enabledDisplayRemarks,
        setEnabledDisplayRemarks,
        addProductDialog,
        addProductMasterDialog,
        addProductDetailMasterDialog,
        printPreviewDialog,
        addingProduct,
        openAddProductDialog,
        openPreviewDialog,
        closePreviewDialog,
        hasProductWithSupply,
        page,
        maxPage: Math.max(1, Math.ceil(filteredSupplyForms.length / SUPPLY_TABLE_ROW_COUNT_PER_PAGE)),
        movePage,
        isProceeding,
        productHistoryModal: {
            ...productHistoryModal,
            // Override with combined handlers that update both main table and sidebar
            handleHide: handleCombinedHide,
            handleBack: handleCombinedBack,
            handleBackAt: handleCombinedBackAt,
        },
        // Combined Right Sidebar
        rightSidebarOpen,
        rightSidebarMode,
        toggleRightSidebar,
        closeSidebar,
        openSidebar,
        switchRightSidebarMode,
        handleAddProductFromSidebar,
        handleAddProductFromSidebarAt,
        handleOpenAddProductMasterFromSidebar,
        handleOpenAddProductDetailMasterFromSidebar,
        sidebarRefreshTrigger,
        sidebarScrollToProductId,
        setSidebarScrollToProductId,
        supplyHistoryAll,
        submitVersion,
        navigateToPreviousPage,
        navigateToNextPage,
        navigateToPage
    }
}

export default useSupplyListForm;

/** 選択中の発注先を取得する
 * @param supplierGroups 発注先(グループ)のリスト
 * @param supplierGroupNumber 発注先(グループ)の選択番号
 * @return 発注先(荷主)情報
 */
const getSelectingSupplierGroup = (supplierGroups, supplierGroupNumber) => {
    return supplierGroups.find((supplierGroup) => supplierGroup.supplierGroupNumber === supplierGroupNumber);
}

/**
 * supplyDetail / supplyHistory の同一商品判定キーを生成する
 */
const makeHistoryKey = (item) => [
    item.productId,
    item.supplyTypeId ?? "",
    item.originId ?? "",
    item.rankId ?? "",
    item.productClassId ?? "",
    item.packingId ?? "",
    item.weight ?? "",
    item.weightUnitId ?? "",
].join("-")

/**
 * ProductDetailInfo → SupplyDetail変換
 * サイドバー・ダイアログから追加する際に、APIを呼ばずにローカルでSupplyDetailを構築する
 * @param productData 商品明細情報 (ProductDetailInfo)
 * @param supplyTypeId 発注種別ID
 */
const buildSupplyDetailFromProduct = (productData, supplyTypeId) => {
    const remark = productData.remark ?? productData.remarks ?? null;
    return {
        productId: productData.productId,
        productName: productData.productName,
        productDetailId: productData.productDetailId,
        supplyTypeId: supplyTypeId,
        supplyCommodityOwnerCd: productData.supplyCommodityOwnerCd ?? null,
        productDivisionId: productData.productDivisionId ?? null,
        productClassLId: productData.productClassLId ?? null,
        productClassMId: productData.productClassMId ?? null,
        productClassSId: productData.productClassSId ?? null,
        originId: productData.originId ?? null,
        originName: productData.originName ?? productData.origin ?? null,
        rankId: productData.rankId ?? null,
        rankName: productData.rankName ?? productData.rank ?? null,
        productClassId: productData.productClassId ?? null,
        productClassName: productData.productClassName ?? productData.productClass ?? null,
        packingId: productData.packingId ?? null,
        packingName: productData.packingName ?? productData.packing ?? null,
        otherSpec: productData.otherSpec ?? null,
        weight: productData.weight != null ? Number(productData.weight) || null : null,
        weightUnitId: productData.weightUnitId != null ? Number(productData.weightUnitId) || null : null,
        weightUnit: productData.weightUnit || productData.weightUnitName || null,
        weightUnitName: productData.weightUnitName || productData.weightUnit || null,
        supplyUnitId: productData.supplyUnitId ?? productData.orderingUnitId ?? null,
        supplyUnitName: productData.supplyUnitName ?? productData.orderingUnit ?? null,
        // 備考 (FreeProduct や履歴 row から引き継ぐ。バックエンド側は後追いで対応)
        remark,
        remarks: remark,
        // 初期 supplyQuantityDetail に remark を反映 → 既存の備考入力欄にそのまま表示される。
        // 空 [] を渡すと createSupplyDetailsForForm がデフォルト ({remarks: ""}) を生成するため、
        // ここで明示的に 1 行作って remarks をプリセットする。
        supplyQuantityDetails: [{
            supplyQuantity: null,
            supplyUnitQuantity: null,
            remarks: remark ?? "",
        }],
        isDelete: false,
    };
}
